import { useRef, useState } from "react";

import { TagsInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconUpload, IconX } from "@tabler/icons-react";
import {
  Button,
  Modal,
  ModalActions,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalTitle,
} from "@onengine/component-library";

import { useFileManager, useFileManagerUI } from "@/components/core/FileManager";
import type { UploadFileItem } from "@/components/core/FileManager/FileManager.types";
import { isSystemTag } from "@/components/features/FileManager/fileTags.utils";
import { formatFileSize } from "@/utils/formatFileSize.ts";
import { clsx } from "clsx";

import styles from "./UploadModal.module.scss";

export function UploadModal() {
  const { upload, isUploading } = useFileManager();
  const { isUploadOpen, closeUpload } = useFileManagerUI();
  const [files, setFiles] = useState<UploadFileItem[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function addFiles(incoming: FileList | null) {
    if (!incoming) return;
    const newItems: UploadFileItem[] = Array.from(incoming).map((f) => ({
      file: f,
      name: f.name,
      size: f.size,
      type: f.type,
    }));
    setFiles((prev) => {
      const existingNames = new Set(prev.map((p) => p.name));
      return [...prev, ...newItems.filter((item) => !existingNames.has(item.name))];
    });
  }

  function removeFile(name: string) {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  }

  function handleClose() {
    setFiles([]);
    setTags([]);
    if (inputRef.current) inputRef.current.value = "";
    closeUpload();
  }

  function handleSubmit() {
    if (files.length === 0) return;
    const cleanTags = tags.map((t) => t.trim()).filter((t) => t && !isSystemTag(t));
    const filesToUpload = cleanTags.length ? files.map((f) => ({ ...f, tags: cleanTags })) : files;
    upload(filesToUpload, undefined, {
      onSuccess: () => {
        handleClose();
      },
      onError: () => {
        notifications.show({
          title: "Upload failed",
          message: "Could not upload files. Please try again.",
          color: "red",
          autoClose: 4000,
        });
      },
    });
  }

  return (
    <Modal open={isUploadOpen} onClose={handleClose}>
      <ModalContent>
        <ModalTitle>Upload files</ModalTitle>
        <ModalBody>
          <div className={styles.body}>
            <div
              className={clsx(styles.dropZone, isDragOver && styles.dragOver)}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
            >
              <IconUpload size={28} color="#646d7e" />
              <p className={styles.dropZoneText}>
                Drag &amp; drop files here, or <span className={styles.browseLink}>browse</span>
              </p>
              <input
                ref={inputRef}
                type="file"
                multiple
                className={styles.hiddenInput}
                onChange={(e) => addFiles(e.target.files)}
              />
            </div>

            {files.length > 0 && (
              <div className={styles.fileList}>
                {files.map((f) => (
                  <div key={f.name} className={styles.fileRow}>
                    <span className={styles.fileName}>{f.name}</span>
                    <span className={styles.fileSize}>{formatFileSize(f.size)}</span>
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => removeFile(f.name)}
                      aria-label={`Remove ${f.name}`}
                    >
                      <IconX size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <TagsInput
              label="Tags"
              placeholder="Add a tag and press Enter"
              description="Optional — applied to every file in this upload"
              value={tags}
              onChange={setTags}
              clearable
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalActions position="right">
            <Button type="button" variant="neutral-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={files.length === 0 || isUploading}
            >
              {isUploading ? "Uploading…" : `Upload${files.length > 0 ? ` (${files.length})` : ""}`}
            </Button>
          </ModalActions>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
